// Greek RES + storage regulatory framework (Category 11A / 11B).
// Mirrors the backend `BESS_feasibility/Configuration/regulatory_framework.py`
// so the payload emits exactly the keys `normalize_regulatory_framework_config()`
// expects. Keep the two files in sync.
//
// mode "none" (default) leaves every existing run unchanged — the backend
// activates no constraint or settlement change.
//
// Category 11A — Per-source remuneration:
//   * PV energy paid at a FIXED Reference Price (EUR/MWh) all year.
//   * Battery energy paid at the DAM price.
//   * Battery charges ONLY from PV surplus (grid charging forbidden).
//   * Static injection-cap profile at the POI (e.g. 60% during 07:00-19:00).
//
// Category 11B — Single market remuneration:
//   * All injected energy (PV + battery) paid at the DAM price.
//   * Battery may charge from PV or the grid (more arbitrage).
//   * Time-varying injection-cap profile (dynamic limits per hour of day).

export const REGULATORY_FRAMEWORK_MODES = [
  { value: "none", label: "None — standard merchant settlement" },
  { value: "11A", label: "Category 11A — Per-source remuneration (PV at Reference Price, BESS at DAM)" },
  { value: "11B", label: "Category 11B — Single market remuneration (PV + BESS at DAM)" },
];

export const REGULATORY_FRAMEWORK_HELPER_TEXT =
  "Ahead of the granting of Connection Terms (Οροι Συνδεσης) for RES projects with " +
  "storage in Greece, hybrid stations fall under Category 11A or 11B. The category " +
  "determines how injected energy is remunerated, from which sources the battery may " +
  "charge, and the injection limits at the POI. Selecting a category enforces its " +
  "rules as hard constraints in the dispatch optimization and documents them in the " +
  "bankability report. Leave as 'None' to keep the standard merchant behaviour.";

export const REGULATORY_MODE_DESCRIPTIONS = {
  none:
    "No regulatory framework: all exported energy is settled at the market price and " +
    "no injection-cap profile is enforced (current standard behaviour).",
  "11A":
    "Per-source remuneration: PV energy is paid a fixed, predictable Reference Price " +
    "(Τιμή Αναφοράς) for the whole year, while battery energy is paid at the variable " +
    "DAM price. The battery charges ONLY from PV surplus or curtailed PV energy — grid " +
    "charging is forbidden (the toggle is locked OFF). The station observes a static " +
    "daily injection-cap profile at the POI (e.g. 60% of the connection capacity " +
    "during 07:00-19:00, no cap for the remaining hours). The Market Revenue Stack is " +
    "restricted to DAM.",
  "11B":
    "Single market remuneration: all injected energy (PV and battery) is paid at the " +
    "DAM price as one dynamic revenue stream. The battery may charge from the PV or " +
    "from the grid — more arbitrage opportunities. The station observes a time-varying " +
    "injection-cap profile whose limit changes by hour of the day.",
};

export const INJECTION_CAP_INPUT_MODE_OPTIONS = [
  { value: "percent_of_basis", label: "Percent of capacity basis (%)" },
  { value: "absolute_kw", label: "Absolute limit (kW)" },
];

export const INJECTION_CAP_BASIS_OPTIONS = [
  { value: "connection_capacity", label: "Contracted connection capacity at the POI (grid export limit)" },
  { value: "pv_installed", label: "Installed PV capacity" },
  { value: "pv_plus_bess", label: "Installed PV capacity + BESS discharge power" },
];

export const INJECTION_CAP_HELPER_TEXT =
  "When a category (11A/11B) is selected, the contracted connection capacity " +
  "(grid export / import limits below in Grid Connection) is always enforced as a " +
  "24/7 hard limit at the POI. The windows below TIGHTEN the export limit during " +
  "specific hours; the daily profile repeats for every day of the simulated year. " +
  "Hours not covered by a window fall back to the connection capacity. PV energy " +
  "above the cap that cannot be stored in the battery is curtailed by the " +
  "optimizer. Enter the cap either as a percentage of the selected capacity basis " +
  "or as an absolute kW value.";

export const DISPATCH_WINDOW_HELPER_TEXT =
  "Optional daily windows restricting WHEN the battery may charge or discharge. " +
  "Disabled (default) means any time of day — the current behaviour. Hours are " +
  "inclusive; windows may wrap midnight (e.g. 22:00 to 05:00).";

export const REFERENCE_PRICE_INDEXATION_OPTIONS = [
  {
    value: "flat_nominal",
    label: "Fixed in nominal terms (the award's own basis)",
    description:
      "A Reference Price awarded under the framework is fixed for the support period, so the " +
      "market price projection is applied only to the battery-at-DAM share of the settlement. " +
      "This is the default and the defensible basis.",
  },
  {
    value: "inflation_indexed",
    label: "Indexed to the stated inflation rate",
    description:
      "Use only if the award actually carries an indexation clause. The Reference Price leg then " +
      "grows with the inflation rate entered above, not with the market price path.",
  },
  {
    value: "same_as_market",
    label: "Follows the market price projection (legacy)",
    description:
      "Reproduces the pre-August-2026 behaviour, in which a price escalation was applied to the " +
      "regulated leg as well. Kept only for reconciling against an older report - it overstates " +
      "revenue whenever an escalating projection mode is selected.",
  },
];

export const DEFAULT_REGULATORY_FRAMEWORK = {
  mode: "none",
  // 11A only: fixed PV Reference Price in EUR/MWh (constant across the year).
  pvReferencePriceEurMwh: "",
  // 11A only: how that Reference Price travels over the horizon. Default is the
  // award's own basis - fixed in nominal terms.
  referencePriceIndexation: "flat_nominal",
  injectionCap: {
    enabled: false,
    inputMode: "percent_of_basis", // percent_of_basis | absolute_kw
    basis: "connection_capacity",  // connection_capacity | pv_installed | pv_plus_bess
    // Daily windows; hours inclusive (0-23). capValue is % of basis or kW
    // depending on inputMode. Overlaps resolve to the most conservative cap.
    windows: [{ startHour: 7, endHour: 19, capValue: 60 }],
  },
  chargeWindow: { enabled: false, startHour: 0, endHour: 23 },
  dischargeWindow: { enabled: false, startHour: 0, endHour: 23 },
};

export function normalizeRegulatoryFramework(cfg) {
  const raw =
    (cfg && cfg.arbitrageMerchant && cfg.arbitrageMerchant.regulatoryFramework) ||
    (cfg && cfg.regulatoryFramework) ||
    {};
  const mode = ["11A", "11B"].includes(String(raw.mode || "").toUpperCase())
    ? String(raw.mode).toUpperCase()
    : "none";
  return {
    ...DEFAULT_REGULATORY_FRAMEWORK,
    ...raw,
    mode,
    injectionCap: {
      ...DEFAULT_REGULATORY_FRAMEWORK.injectionCap,
      ...(raw.injectionCap || {}),
      windows:
        Array.isArray(raw.injectionCap?.windows) && raw.injectionCap.windows.length
          ? raw.injectionCap.windows
          : DEFAULT_REGULATORY_FRAMEWORK.injectionCap.windows,
    },
    chargeWindow: { ...DEFAULT_REGULATORY_FRAMEWORK.chargeWindow, ...(raw.chargeWindow || {}) },
    dischargeWindow: { ...DEFAULT_REGULATORY_FRAMEWORK.dischargeWindow, ...(raw.dischargeWindow || {}) },
  };
}

export function validateRegulatoryFramework(rf) {
  const errors = {};
  if (!rf || rf.mode === "none") return errors;
  if (rf.mode === "11A") {
    const price = Number(rf.pvReferencePriceEurMwh);
    if (!Number.isFinite(price) || price <= 0) {
      errors.pvReferencePriceEurMwh =
        "Category 11A requires a positive PV Reference Price (EUR/MWh).";
    }
  }
  if (rf.injectionCap?.enabled) {
    const windows = rf.injectionCap.windows || [];
    if (!windows.length) {
      errors.injectionCap = "Add at least one injection-cap window or disable the cap.";
    } else if (
      windows.some(
        (w) => !Number.isFinite(Number(w.capValue)) || Number(w.capValue) < 0
      )
    ) {
      errors.injectionCap = "Every cap window needs a non-negative cap value.";
    }
  }
  return errors;
}
